import React, { useMemo } from "react";
import { DirectorLevel, TYLevel } from "../constants";

const STATUS = {
  1: { label: "Не согласовано", tone: "wait" },
  2: { label: "На доработке", tone: "rework" },
  3: { label: "Согласовано", tone: "ok" },
};

const STAGES = [
  {
    key: "director",
    title: "Директор",
    statusKey: "director_status",
    nameKey: "director_surname_name",
    commentKey: "director_comment",
  },
  {
    key: "ty",
    title: "ТУ",
    statusKey: "TY_status",
    nameKey: "TY_surname_name",
    commentKey: "TY_comment",
  },
];

function formatShortDate(dateString) {
  if (!dateString) return "";
  const iso = String(dateString).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function statusMeta(statusId) {
  return STATUS[Number(statusId)] || STATUS[1];
}

function overallCopy(data) {
  const ty = Number(data?.TY_status);
  const director = Number(data?.director_status);
  if (ty === 3) {
    return {
      tone: "ok",
      title: "Деп в расписании",
      text: "ТУ согласовал график, правки закрыты.",
    };
  }
  if (director === 3) {
    return {
      tone: "rework",
      title: "Ждёт ТУ",
      text: "Директор отправил деп, территориальный управляющий ещё нет.",
    };
  }
  if (ty === 2 || director === 2) {
    return {
      tone: "rework",
      title: "На доработке",
      text: "График вернули на доработку.",
    };
  }
  return {
    tone: "wait",
    title: "Черновик",
    text: "Можно сохранить черновик или отправить согласование.",
  };
}

const AutoDepApprove = ({
  data,
  weekDates = [],
  accessLevel,
  status,
  comment,
  onStatusChange,
  onCommentChange,
  onSave,
  onSaveDraft,
  saving = false,
  savingDraft = false,
  message,
  locked = false,
}) => {
  const isDirector = Number(accessLevel) <= DirectorLevel;
  const isTy = Number(accessLevel) >= TYLevel;
  const canEdit = !locked && (isDirector || isTy);
  const canDraft = !locked && isDirector;
  const myKey = isDirector ? "director" : isTy ? "ty" : null;

  const summary = useMemo(() => overallCopy(data), [data]);
  const weekLabel =
    weekDates[0] && weekDates.at(-1)
      ? `${formatShortDate(weekDates[0])} — ${formatShortDate(weekDates.at(-1))}`
      : "";

  return (
    <div className="chk-approve autodep-approve">
      <div className={`chk-approve-summary is-${summary.tone}`}>
        <div>
          <h4>{summary.title}</h4>
          <p>{summary.text}</p>
        </div>
        {weekLabel ? <span className="chk-approve-week">{weekLabel}</span> : null}
      </div>

      <div className="chk-approve-pipeline">
        {STAGES.map((stage, index) => {
          const meta = statusMeta(data?.[stage.statusKey]);
          const isMine = myKey === stage.key;
          return (
            <React.Fragment key={stage.key}>
              {index > 0 ? <div className="chk-approve-arrow" aria-hidden="true" /> : null}
              <article
                className={`chk-approve-card is-${meta.tone} ${isMine ? "is-mine" : ""}`}
              >
                <header>
                  <span>{stage.title}</span>
                  {isMine ? <em>вы</em> : null}
                </header>
                <strong className={`chk-approve-badge is-${meta.tone}`}>
                  {meta.label}
                </strong>
                <p className="chk-approve-name">
                  {data?.[stage.nameKey] || "Ещё не указан"}
                </p>
                <p className="chk-approve-comment">
                  {data?.[stage.commentKey] || "Нет комментария"}
                </p>
              </article>
            </React.Fragment>
          );
        })}
      </div>

      {canEdit ? (
        <div className="chk-approve-form">
          <h5>Ваше согласование{myKey === "ty" ? " · ТУ" : " · Директор"}</h5>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label" htmlFor="autodep-approve-status">
                Статус
              </label>
              <select
                id="autodep-approve-status"
                className="form-select"
                value={status}
                onChange={(event) => onStatusChange(event.target.value)}
              >
                <option value="1">Не согласовано</option>
                <option value="2">На доработке</option>
                <option value="3">Согласовано</option>
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label" htmlFor="autodep-approve-comment">
                Комментарий
              </label>
              <textarea
                id="autodep-approve-comment"
                className="form-control"
                rows={3}
                value={comment}
                onChange={(event) => onCommentChange(event.target.value)}
              />
            </div>
          </div>
          <div className="chk-approve-form-footer">
            {message ? (
              <span className={message.ok ? "is-ok" : "is-error"}>{message.text}</span>
            ) : (
              <span className="chk-more-footer-hint">
                {canDraft
                  ? "Черновик не отправляет деп ТУ, только сохраняет слоты."
                  : "Сохраните статус и комментарий."}
              </span>
            )}
            <div className="autodep-approve-actions">
              {canDraft ? (
                <button
                  type="button"
                  className="schedule-chk-week-tool-btn"
                  onClick={onSaveDraft}
                  disabled={saving || savingDraft}
                >
                  {savingDraft ? "Сохранение…" : "Сохранить черновик"}
                </button>
              ) : null}
              <button
                type="button"
                className="btn"
                onClick={onSave}
                disabled={saving || savingDraft}
              >
                {saving ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="chk-approve-readonly">
          {locked
            ? "Деп уже в расписании, редактирование закрыто."
            : "Менять статус могут директор и ТУ."}
        </p>
      )}
    </div>
  );
};

export default AutoDepApprove;
